'use client'

import { Carousel, type CarouselNavigationRenderProps } from '@/components/carousel'
import Link from '@/components/Link'
import { type FragmentType, graphql, useFragment } from '@/generated/gql'
import type { Coordinates, VendorHighlightCategoriesFilterInput } from '@/generated/graphql'
import { ServiceType } from '@/generated/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useLazyQuery } from '@apollo/client'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Button, ButtonBase, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import sortBy from 'lodash/sortBy'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { roundRating } from '../../../common/utils/rating'
import { getDeliverySummaryFromVendor } from '../delivery-summary/getDeliverySummary'
import SupplierCard from '../supplier-card/supplier-card'

export const vendorHighlightListFragment = graphql(`
  fragment VendorHighlightList_Fragment on VendorHighlightCategoryVendorsPaged {
    pageInfo {
      offset
      limit
      hasMore
      count
    }
    vendors {
      id
      currency
      permalink
      companyName
      logo
      images {
        alt
        id
        secureUrl
        position
      }
      ratingOutOfFive: rating(input: { maxRating: 5 })
      ratingOutOfFour: rating(input: { maxRating: 4 })
      totalRatings
      gmRating
      gmTotalRatings
      inRangeLocations(lat: $lat, lng: $lng, serviceType: $serviceType, dateTime: $dateTime) {
        id
        label
        details {
          effectiveMinimumNoticePeriod
        }
        deliveryRegions(input: { location: { lat: $lat, lng: $lng }, serviceType: $serviceType }) {
          id
          serviceType
          orderPosition
          minOrderValueExTax
          deliveryFee {
            total
            totalExTax
          }
        }
      }
    }
  }
`)

export const highlightCategoryWithVendorsFragment = graphql(`
  fragment HighlightCategoryWithVendors_Fragment on VendorHighlightCategory {
    id
    label
    name
    vendors(limit: 5) {
      ...VendorHighlightList_Fragment
    }
  }
`)

const highlightListMoreVendorQuery = graphql(`
  query CateringHighlightList_MoreVendorQuery(
    $location: Coordinates!
    $highlightCategoryId: String!
    $limit: Int
    $offset: Int
    $lat: Float!
    $lng: Float!
    $serviceType: ServiceType!
    $dateTime: VendorListDateSearchInput
    $filters: VendorHighlightCategoriesFilterInput!
  ) {
    vendorsForHighlightCategoryForLocation(
      location: $location
      highlightCategoryId: $highlightCategoryId
      limit: $limit
      offset: $offset
      filters: $filters
    ) {
      ...VendorHighlightList_Fragment
    }
  }
`)

type Props = {
  highlightCategoryData: FragmentType<typeof highlightCategoryWithVendorsFragment>
  onViewMoreClick: (tag: string) => void
  vendorData: FragmentType<typeof vendorHighlightListFragment>
  location: Coordinates
  highlightFilters: VendorHighlightCategoriesFilterInput
}

const BASE_SLIDE_WIDTH = { sm: 260, md: 261 }
const MAX_VISIBLE = { sm: 2, md: 3, lg: 4, xl: 5 }
const MAX_SLIDE_SCALE = 1.5

const SupplierHighlightList: React.FC<Props> = ({
  onViewMoreClick,
  vendorData,
  highlightCategoryData,
  location,
  highlightFilters,
}) => {
  const latitude = location.latitude
  const longitude = location.longitude
  const { t } = useTranslation()
  const showFiveStarRating = useFeatureFlag(SPLITS.FIVE_STAR_RATINGS)
  const gmRatingDisplayEnabled = useFeatureFlag(SPLITS.GM_ORDER_RATING_DISPLAY)
  const { vendors, pageInfo } = useFragment(vendorHighlightListFragment, vendorData)
  const {
    id: highlightCategoryId,
    label,
    name: highlightName,
  } = useFragment(highlightCategoryWithVendorsFragment, highlightCategoryData)
  const serviceType = highlightFilters.serviceType ?? ServiceType.Gm
  const dateTime = highlightFilters.dateTime ?? null

  const [gettingMore, setGettingMore] = useState(false)
  const [getMoreVendors] = useLazyQuery(highlightListMoreVendorQuery)
  const [offset, setOffset] = useState<number>(vendors.length)
  const [hasMore, setHasMore] = useState(pageInfo.hasMore)
  const [moreVendors, setMoreVendors] = useState<typeof vendors>([])
  const getMoreLimit = 5

  const vendorsToShow = useMemo(() => {
    const uniqueVendors = new Map<string, (typeof vendors)[number]>()
    for (const vendor of vendors) {
      uniqueVendors.set(vendor.id, vendor)
    }
    for (const vendor of moreVendors) {
      uniqueVendors.set(vendor.id, vendor)
    }
    return Array.from(uniqueVendors.values())
  }, [vendors, moreVendors])

  const [latestMoreVendorData, setLatestMoreVendorData] = useState<FragmentType<
    typeof vendorHighlightListFragment
  > | null>(null)

  // Always call useFragment, but it will return null if data is null
  const latestVendorData = useFragment(vendorHighlightListFragment, latestMoreVendorData)

  useEffect(() => {
    setMoreVendors([])
    setOffset(vendors.length)
    setHasMore(pageInfo.hasMore)
    setGettingMore(false)
    setLatestMoreVendorData(null)
  }, [pageInfo.hasMore, vendors])

  const handleShowMore = useCallback(async () => {
    try {
      const moreVendorData = await getMoreVendors({
        variables: {
          highlightCategoryId,
          location,
          limit: getMoreLimit,
          offset,
          lat: latitude,
          lng: longitude,
          serviceType,
          dateTime,
          filters: highlightFilters,
        },
      })

      const vendorsForHighlightCategoryForLocation =
        moreVendorData.data?.vendorsForHighlightCategoryForLocation

      if (!vendorsForHighlightCategoryForLocation) {
        setHasMore(false)
        setGettingMore(false)
      } else {
        // Store the data to be processed by useFragment at the top level
        setLatestMoreVendorData(vendorsForHighlightCategoryForLocation)
      }
    } catch (error) {
      setGettingMore(false)
    }
  }, [
    dateTime,
    getMoreVendors,
    highlightCategoryId,
    highlightFilters,
    latitude,
    location,
    longitude,
    offset,
    serviceType,
  ])

  // Process the fragment data when it changes
  useEffect(() => {
    if (latestVendorData) {
      const { vendors: newVendors, pageInfo: moreVendorsPageInfo } = latestVendorData
      setHasMore(moreVendorsPageInfo.hasMore)
      setOffset((prev) => prev + Number(newVendors.length))
      setMoreVendors((prev) => {
        const deduped = new Map<string, (typeof vendors)[number]>()
        for (const vendor of prev) {
          deduped.set(vendor.id, vendor)
        }
        for (const vendor of newVendors) {
          deduped.set(vendor.id, vendor)
        }
        return Array.from(deduped.values())
      })
      setGettingMore(false)
      setLatestMoreVendorData(null) // Clear the data after processing
    }
  }, [latestVendorData])

  const requestMoreVendors = useCallback(() => {
    if (gettingMore || !hasMore) return
    setGettingMore(true)
    void handleShowMore()
  }, [gettingMore, hasMore, handleShowMore])

  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const spaceBetween = isMdUp ? 12 : 8

  const vendorsToShowCount = vendorsToShow.length

  const slideWidth = useMemo(() => {
    const count = vendorsToShowCount
    const gap = spaceBetween
    const smMax = Math.round(BASE_SLIDE_WIDTH.sm * MAX_SLIDE_SCALE)
    const mdMax = Math.round(BASE_SLIDE_WIDTH.md * MAX_SLIDE_SCALE)

    const calcWidth = (visible: number, maxPx: number) => {
      if (count <= visible) {
        // All cards visible — fill space equally, capped at 150% of base
        return `min(calc((100% - ${(visible - 1) * gap}px) / ${visible}), ${maxPx}px)`
      }
      // More cards than visible — use equal share (will scroll), no cap needed
      return `calc((100% - ${(visible - 1) * gap}px) / ${visible})`
    }

    const smVisible = Math.min(count, MAX_VISIBLE.sm)
    const mdVisible = Math.min(count, MAX_VISIBLE.md)
    const lgVisible = Math.min(count, MAX_VISIBLE.lg)
    const xlVisible = Math.min(count, MAX_VISIBLE.xl)

    return {
      xs: 'calc(100% - 24px)',
      sm: calcWidth(smVisible, smMax),
      md: calcWidth(mdVisible, mdMax),
      lg: calcWidth(lgVisible, mdMax),
      xl: calcWidth(xlVisible, mdMax),
    }
  }, [vendorsToShowCount, spaceBetween])

  const renderNavigation = useMemo(() => {
    const NavigationComponent = ({
      isBeginning,
      isEnd,
      slideNext,
      slidePrev,
    }: CarouselNavigationRenderProps) => {
      const hasMultipleVendors = vendorsToShowCount > 1
      const showPrev = hasMultipleVendors && !isBeginning
      const showNext = hasMore || !isEnd

      if (!showPrev && !showNext) return null

      const sharedStyles = {
        alignItems: 'center',
        backgroundColor: alpha(theme.palette.primary.main, 0.3),
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 48,
        zIndex: 10,
        transition: theme.transitions.create(['opacity', 'background-color'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.4),
        },
      } as const

      const handlePrev = () => {
        if (showPrev) {
          slidePrev()
        }
      }

      const handleNext = () => {
        if (isEnd && hasMore) {
          requestMoreVendors()
        }
        if (!isEnd) {
          slideNext()
        }
      }

      return (
        <>
          {showPrev ? (
            <ButtonBase
              aria-label={t('marketplace:previous')}
              focusRipple={false}
              sx={{
                ...sharedStyles,
                borderBottomRightRadius: theme.spacing(2),
                borderTopRightRadius: theme.spacing(2),
                left: 0,
              }}
              onClick={handlePrev}>
              <Stack alignItems="center" justifyContent="center" height="100%">
                <ChevronLeft htmlColor={theme.palette.common.black} />
              </Stack>
            </ButtonBase>
          ) : null}
          {showNext ? (
            <ButtonBase
              aria-label={t('marketplace:next')}
              focusRipple={false}
              sx={{
                ...sharedStyles,
                borderBottomLeftRadius: theme.spacing(2),
                borderTopLeftRadius: theme.spacing(2),
                right: 0,
              }}
              onClick={handleNext}>
              <Stack alignItems="center" justifyContent="center" height="100%">
                <ChevronRight htmlColor={theme.palette.common.black} />
              </Stack>
            </ButtonBase>
          ) : null}
        </>
      )
    }

    NavigationComponent.displayName = 'NavigationComponent'
    return NavigationComponent
  }, [hasMore, requestMoreVendors, t, theme, vendorsToShowCount])

  if (!vendorsToShow.length) return null

  return (
    <Stack spacing={1} direction="column" width="100%">
      <Stack alignItems="center" direction="row" spacing={2}>
        <Typography variant="h5">{label}</Typography>
        <Button size="small" variant="text" onClick={() => onViewMoreClick(highlightName)}>
          <Typography color="primary.main" variant="body2" fontWeight={600}>
            {t('marketplace:viewAll')}
          </Typography>
        </Button>
      </Stack>
      <Carousel
        allowTouchMove
        onReachEnd={requestMoreVendors}
        renderNavigation={renderNavigation}
        slideClassName="supplier-highlight-slide"
        slidesPerView="auto"
        spaceBetween={spaceBetween}
        sx={{
          overflow: 'hidden',
          pr: 1,
          '& .swiper': { overflow: 'hidden' },
          '& .swiper-wrapper': { alignItems: 'stretch' },
          '& .supplier-highlight-slide': {
            display: 'flex',
            height: 'auto',
            paddingBottom: theme.spacing(0.5),
            minWidth: slideWidth,
            maxWidth: slideWidth,
          },
          '& .supplier-highlight-slide > *': { width: '100%' },
        }}>
        {vendorsToShow.map((vendor) => {
          const vendorImage = sortBy(vendor.images || [], 'position')[0]
          const legacyRating = showFiveStarRating ? vendor.ratingOutOfFive : vendor.ratingOutOfFour
          const hasGmRating = (vendor.gmTotalRatings ?? 0) > 0
          const vendorRating = gmRatingDisplayEnabled
            ? roundRating(vendor.gmRating ?? 0)
            : legacyRating
          const deliverySummary = getDeliverySummaryFromVendor(vendor)
          return (
            <Box
              key={vendor.id}
              sx={{
                ...(highlightFilters.itemSearch && {
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  padding: 1,
                  boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.45)}`,
                }),
              }}>
              <Link
                href={{
                  pathname: `/office-catering/vendors/${vendor.permalink}`,
                  query: {
                    source: highlightFilters.itemSearch ? 'item-search' : 'carousel',
                    preventAutoBasketSelection: 'true',
                    ...(highlightFilters.itemSearch
                      ? { itemSearchTerm: highlightFilters.itemSearch }
                      : {}),
                  },
                }}
                style={{ display: 'block', height: '100%', width: '100%' }}>
                <SupplierCard
                  companyName={vendor.companyName}
                  logo={vendor.logo}
                  image={vendorImage}
                  rating={vendorRating}
                  totalRatings={gmRatingDisplayEnabled ? undefined : vendor.totalRatings}
                  showRating={gmRatingDisplayEnabled ? hasGmRating : undefined}
                  highlightName={highlightName}
                  width="100%"
                  mediaHeight={isMdUp ? 141 : undefined}
                  mediaAspectRatio={16 / 9}
                  minRatingToShow={2}
                  vendor={{ id: vendor.id, currency: vendor.currency }}
                  deliverySummary={deliverySummary}
                />
              </Link>
            </Box>
          )
        })}
        {gettingMore
          ? Array.from({ length: getMoreLimit }, (_, index) => (
              <Skeleton
                key={`loading-${index}`}
                variant="rectangular"
                height={200}
                sx={{ width: '100%' }}
              />
            ))
          : null}
      </Carousel>
      {/* {hasMore && !gettingMore ? (
        <Box display="flex" justifyContent={isMdUp ? 'flex-end' : 'center'} mt={1}>
          <Button onClick={requestMoreVendors} variant="outlined" sx={{ minWidth: 160 }}>
            {t('marketplace:more')}
          </Button>
        </Box>
      ) : null} */}
    </Stack>
  )
}

export default SupplierHighlightList
